import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const ThankYou = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch Order Details
  useEffect(() => {
    fetch(`https://server.enjoywiki.com/youtubeai-may/order/${id}`)
      .then((res) => res.json())
      .then((data) => {
        setOrder(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching order:", err);
        setLoading(false);
      });
  }, [id]);

  // Handle Print Action
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light" style={{ marginTop: "80px" }}>
        <div className="text-center">
          <h2 className="text-danger fw-bold">Order Not Found</h2>
          <p className="text-muted">We couldn't retrieve the receipt details.</p>
          <Link to="/" className="btn btn-primary mt-3 px-4 rounded-pill">
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Payment Successful | Receipt</title>
        <meta name="robots" content="noindex" />
        {/* Bootstrap Icons */}
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css"
        />
      </Helmet>

      {/* 
         PRINT STYLES: 
         Hides everything except the #printable-card-area
      */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #printable-card-area, #printable-card-area * {
              visibility: visible;
            }
            #printable-card-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 0;
              border: none !important;
              box-shadow: none !important;
            }
            .no-print {
              display: none !important;
            }
            @page {
              margin: 0.5cm;
              size: auto;
            }
          }
        `}
      </style>

      {/* Main Container with Top Margin to avoid Header Conflict */}
      <div className="bg-light py-5" style={{ minHeight: "100vh",}}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8" style={{ marginTop: "120px" }}>
              
              {/* THIS CARD IS THE RECEIPT */}
              <div id="printable-card-area" className="card shadow-lg border-0 rounded-4 overflow-hidden bg-white">
                
                {/* 1. Icon Inside Header */}
                <div className="card-header bg-success text-white text-center py-5 border-0">
                  <div className="mb-3">
                    {/* Large Icon */}
                    <i className="bi bi-check-circle-fill display-1"></i>
                  </div>
                  <h2 className="fw-bold mb-1">Payment Successful</h2>
                  <p className="mb-0 opacity-75">Thank you for your purchase!</p>
                </div>

                <div className="card-body p-4 p-md-5">
                  
                  {/* Amount Section */}
                  <div className="text-center mb-5">
                    <p className="text-uppercase text-muted fw-bold small mb-1">Total Amount Paid</p>
                    <h1 className="text-dark fw-bold mb-0 display-4">${order.totalPrice}</h1>
                    <span className="badge bg-light text-success border border-success mt-2 px-3 py-2 rounded-pill">
                      <i className="bi bi-paypal me-1"></i> Paid via PayPal
                    </span>
                  </div>

                  {/* Receipt Details Table */}
                  <div className="bg-light rounded-3 p-4 mb-4 border">
                    <h5 className="fw-bold text-dark border-bottom pb-3 mb-3">
                      Transaction Receipt
                    </h5>
                    
                    <div className="row mb-3">
                      <div className="col-5 text-secondary">Order ID:</div>
                      <div className="col-7 text-end fw-bold text-dark text-break font-monospace">
                        {order.orderId}
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-5 text-secondary">Date:</div>
                      <div className="col-7 text-end fw-medium text-dark">
                        {new Date().toLocaleDateString()}
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-5 text-secondary">Customer:</div>
                      <div className="col-7 text-end fw-medium text-dark">
                        {order.customerName}
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-5 text-secondary">Email:</div>
                      <div className="col-7 text-end fw-medium text-dark text-break">
                        {order.customerEmail}
                      </div>
                    </div>

                    {order.credit && (
                      <div className="row mb-3">
                        <div className="col-5 text-secondary">Credits:</div>
                        <div className="col-7 text-end fw-medium text-dark">
                          {order.credit}
                        </div>
                      </div>
                    )}

                    <div className="row mt-4 pt-3 border-top border-secondary border-opacity-25 align-items-center">
                      <div className="col-5 fw-bold text-dark">Package:</div>
                      <div className="col-7 text-end fw-bold text-primary fs-5">
                        {order.packageName}
                      </div>
                    </div>
                  </div>

                  {/* Footer Note */}
                  <div className="text-center">
                    <p className="text-muted small mb-0">
                      An email confirmation has been sent to your address.
                      <br />
                      Please keep this receipt for your records.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons (Not Printed) */}
              <div className="row mt-4 g-3 no-print">
                <div className="col-md-6">
                  <Link 
                    to="/user/subscription-history" 
                    className="btn btn-outline-primary w-100 py-3 rounded-pill fw-bold"
                  >
                    <i className="bi bi-speedometer2 me-2"></i> View Subscription History
                  </Link>
                </div>
                <div className="col-md-6">
                  <button 
                    onClick={handlePrint} 
                    className="btn btn-primary w-100 py-3 rounded-pill fw-bold shadow-sm"
                  >
                    <i className="bi bi-printer-fill me-2"></i> Print Receipt
                  </button>
                </div>
              </div>
              
              <div className="text-center mt-4 no-print">
                <Link to="/" className="text-decoration-none text-secondary small">
                  <i className="bi bi-arrow-left me-1"></i> Return to Home
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ThankYou;